import React, { ReactNode, useState } from 'react';
import Toggle from '@amzn/meridian/toggle';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { updateFeatureFlag, UserState } from 'src/store/userSlice';
import ConfirmModal from 'src/components/shared/confirmModal/confirmModal';
import { createToast } from 'src/store/toastsSlice';
import { TOAST_TIMEOUT } from 'src/config/Toast';
import { getObjectEntries } from 'src/utils/helpers';
import useOnDone from 'src/hooks/useOnDone';

// **** Constants **** //

const DISPLAY_FEATURE_NAMES: Record<keyof UserState['featureFlags'], string> = {
  PLACEMENT_TRANSFER: 'Placement Transfer',
  DECISION_PORTAL: 'Decision Portal',
  ATOZ_NOTIFICATIONS: 'AtoZ Notifications',
  JOB_MODIFICATIONS: 'Job Modifications',
  CORRESPONDENCE: 'Correspondence',
  PERSONA: 'Persona',
  TASK_GROUPING: 'Task Grouping',
} as const;

const MODAL_DESCRIPTIONS = {
  Update(isEnabled: boolean, featureDisplayName: string): ReactNode {
    return (
      <>
        Are you sure you want to {isEnabled ? 'enable' : 'disable'} the <b>{featureDisplayName}</b> feature?
      </>
    );
  },
} as const;

// **** Types **** //

interface ModalState {
  isOpen: boolean;
  description: string | ReactNode;
  featureName: string;
  isEnabled?: boolean;
}

// **** Components **** //

/**
 * Default Comonent
 */
const FeatureFlags = () => {
  const dispatch = useAppDispatch();
  const { isLoadingUpdateFeatureFlag, updateFeatureFlagErrorMessage, featureFlags } = useAppSelector(
    (state) => state.user,
  );
  const [disableToggles, setDisableToggles] = useState(false);
  const [modalConfirmationState, setModalConfirmationState] = useState<ModalState>({
    isOpen: false,
    description: '',
    featureName: '',
  });

  // Confirm Update
  const handleToggleClick = (featureDisplayName: string, featureName: string, isEnabled: boolean) =>
    setModalConfirmationState({
      isOpen: true,
      description: MODAL_DESCRIPTIONS.Update(isEnabled, featureDisplayName),
      featureName,
      isEnabled,
    });

  // Close "Confirmation Modal"
  const closeModal = () =>
    setModalConfirmationState({
      isOpen: false,
      description: '',
      featureName: '',
    });

  // When "Confirm" is clicked inside the confirmation modal
  const onConfirmClick = () => {
    const { featureName, isEnabled } = modalConfirmationState;
    setDisableToggles(true);
    const apiFeatureName = featureName.toLowerCase();
    dispatch(updateFeatureFlag({ featureName: apiFeatureName, isEnabled: !!isEnabled }));
  };

  // When finished "Updating" a feature flag
  useOnDone(() => {
    if (!!updateFeatureFlagErrorMessage) {
      createToast({
        type: 'error',
        message: updateFeatureFlagErrorMessage,
        timeout: TOAST_TIMEOUT,
      });
    } else {
      setModalConfirmationState({
        isOpen: false,
        description: '',
        featureName: '',
      });
    }
    setDisableToggles(false);
  }, isLoadingUpdateFeatureFlag);

  return (
    <div
      style={{
        maxWidth: '300px',
        minWidth: '300px',
        margin: '2rem 0 0 2rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem',
      }}
    >
      {/* Header */}
      <Row>
        <Heading level={3}>Feature Flags</Heading>
      </Row>

      {/* Feature Flag List */}
      {getObjectEntries(featureFlags).map(([key, isEnabled]) => {
        const name = DISPLAY_FEATURE_NAMES[key];
        return (
          <Row key={key} alignmentHorizontal="justify" alignmentVertical="center" type="fill" spacingInset="400">
            <Text>{name}</Text>
            <Toggle
              checked={isEnabled}
              onChange={() => handleToggleClick(name, key, !isEnabled)}
              disabled={disableToggles}
            />
          </Row>
        );
      })}

      {/* Modals */}
      <ConfirmModal
        modalDescription={modalConfirmationState.description}
        open={modalConfirmationState.isOpen}
        setOpen={closeModal}
        onConfirm={onConfirmClick}
        showCancelButton={true}
        showSpinner={isLoadingUpdateFeatureFlag}
        disableAutoClose={true}
      />
    </div>
  );
};

// **** Export **** //

export default FeatureFlags;
